import * as THREE from 'three';
import { padGroups, type Brush, type GameMap, type MaterialKey } from '@game/shared';

/** A billboarded text label rendered once into a canvas — used for pad signs. */
const makeTextSprite = (text: string): THREE.Sprite => {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 96;
  const ctx = canvas.getContext('2d')!;
  ctx.font = '900 64px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 10;
  ctx.strokeStyle = 'rgba(10,14,24,0.9)';
  ctx.strokeText(text, 128, 48);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, 128, 48);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, transparent: true }),
  );
  sprite.scale.set(2.4, 0.9, 1);
  return sprite;
};

/**
 * Owns the renderer, camera and static world. Greybox art only: every brush
 * becomes an instanced box, so the whole map is a handful of draw calls and the
 * art pass later can swap materials without touching collision.
 */
export class SceneRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  /** Attach view-model geometry here — it renders after the world, unclipped. */
  readonly viewModelScene = new THREE.Scene();
  readonly viewModelCamera: THREE.PerspectiveCamera;

  /** Hip-fire vertical FOV in degrees; `setFov` scales it for ADS. */
  private baseFov = 90;
  /**
   * All map-owned content (lights, brushes, grid) lives under these groups so
   * switching maps replaces exactly the map and nothing else — the effect pools
   * and the weapon models are added to the same scenes by other systems.
   */
  private readonly mapRoot = new THREE.Group();
  private readonly viewModelLights = new THREE.Group();
  private readonly disposables: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[] =
    [];
  /** Pad materials pulsed each frame, so the queue floor feels alive. */
  private readonly pulsingPads: { material: THREE.Material; base: number }[] = [];

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      // Depth is cleared between world and view model, so no stencil is needed.
      stencil: false,
    });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.autoClear = false;
    this.renderer.shadowMap.enabled = false;
    // Without tone mapping, lighting bright enough to read clearly clips to
    // white; without it the scene reads as murk. Exposure is per-map.
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(this.baseFov, 1, 0.05, 500);
    this.camera.rotation.order = 'YXZ';

    this.viewModelCamera = new THREE.PerspectiveCamera(65, 1, 0.01, 10);

    this.scene.add(this.mapRoot);
    this.viewModelScene.add(this.viewModelLights);

    this.resize();
  }

  loadMap(map: GameMap): void {
    this.clearMap();

    const light = map.lighting;
    this.renderer.toneMappingExposure = light.exposure;

    this.scene.background = new THREE.Color(map.backgroundColor);
    this.scene.fog = new THREE.Fog(map.fogColor, map.fogNear, map.fogFar);

    this.mapRoot.add(
      new THREE.HemisphereLight(
        light.skyColor,
        light.groundColor,
        light.hemisphereIntensity,
      ),
    );
    // Flat fill: with no shadow maps and no bounce, a surface facing away from
    // the sun receives only the hemisphere's ground term and reads as black.
    this.mapRoot.add(
      new THREE.AmbientLight(light.ambientColor, light.ambientIntensity),
    );

    const sun = new THREE.DirectionalLight(light.sunColor, light.sunIntensity);
    sun.position.set(light.sunPosition.x, light.sunPosition.y, light.sunPosition.z);
    this.mapRoot.add(sun);
    // A dim opposing light so the shadowed side of every box still has form.
    const bounce = new THREE.DirectionalLight(
      light.groundColor,
      light.sunIntensity * 0.3,
    );
    bounce.position.set(
      -light.sunPosition.x,
      light.sunPosition.y * 0.4,
      -light.sunPosition.z,
    );
    this.mapRoot.add(bounce);

    // The view model has its own scene and camera, so it needs its own lights —
    // otherwise the weapon renders as a black silhouette.
    this.lightViewModel(light);

    // One InstancedMesh per material keeps the whole map at ~5 draw calls.
    const byMaterial = new Map<MaterialKey, Brush[]>();
    for (const brush of map.brushes) {
      const bucket = byMaterial.get(brush.material);
      if (bucket) bucket.push(brush);
      else byMaterial.set(brush.material, [brush]);
    }

    const unitBox = new THREE.BoxGeometry(1, 1, 1);
    this.disposables.push(unitBox);
    const matrix = new THREE.Matrix4();

    for (const [key, brushes] of byMaterial) {
      const color = map.palette[key];
      // Neon is unlit: MeshBasic ignores every light, so against the lit world
      // it reads as glowing — signage and strips with zero extra cost.
      const material =
        key === 'neon'
          ? new THREE.MeshBasicMaterial({ color })
          : new THREE.MeshLambertMaterial(
              key === 'glass' ? { color, transparent: true, opacity: 0.3 } : { color },
            );
      this.disposables.push(material);

      const mesh = new THREE.InstancedMesh(unitBox, material, brushes.length);
      brushes.forEach((brush, index) => {
        matrix.makeScale(brush.half.x * 2, brush.half.y * 2, brush.half.z * 2);
        matrix.setPosition(brush.center.x, brush.center.y, brush.center.z);
        mesh.setMatrixAt(index, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      mesh.frustumCulled = false;
      this.mapRoot.add(mesh);
    }

    // Queue pads: glowing circles — blue side, red side — with a soft column of
    // light rising from each, arcade-machine style. Visual only; the sim never
    // collides with them.
    const teamColors = [0x4cc2ff, 0xff6b5e] as const;
    for (const pad of map.pads) {
      // Vs-bot rings glow in the map's neon rather than a team color — one
      // ring, not a blue-vs-red pair, so it must not read as a side.
      const color = pad.botsPerPlayer ? map.palette.neon : teamColors[pad.team];

      const fill = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.22,
      });
      const fillGeometry = new THREE.CircleGeometry(pad.radius, 40);
      const fillMesh = new THREE.Mesh(fillGeometry, fill);
      fillMesh.rotation.x = -Math.PI / 2;
      fillMesh.position.set(pad.x, 0.03, pad.z);

      // Bright unlit ring as the outline; its opacity is pulsed per frame.
      const rim = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
      });
      const rimGeometry = new THREE.RingGeometry(pad.radius, pad.radius + 0.18, 48);
      const rimMesh = new THREE.Mesh(rimGeometry, rim);
      rimMesh.rotation.x = -Math.PI / 2;
      rimMesh.position.set(pad.x, 0.04, pad.z);

      // The light column: an open-ended cylinder fading with additive blending.
      // Depth writes off, so players inside it never punch holes in it.
      const beam = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const beamGeometry = new THREE.CylinderGeometry(
        pad.radius * 0.92,
        pad.radius,
        3.2,
        32,
        1,
        true,
      );
      const beamMesh = new THREE.Mesh(beamGeometry, beam);
      beamMesh.position.set(pad.x, 1.6, pad.z);

      this.disposables.push(fill, rim, beam, fillGeometry, rimGeometry, beamGeometry);
      this.pulsingPads.push({ material: rim, base: 0.9 }, { material: beam, base: 0.1 });
      this.mapRoot.add(fillMesh, rimMesh, beamMesh);
    }

    // A floating label over each pad pair — "1V1", "2V2" — so the modes are
    // readable from across the room, the way a real hub signposts its queues.
    for (const group of padGroups(map)) {
      const [blue, red] = group.pads;
      const label = makeTextSprite(group.id.toUpperCase());
      label.position.set((blue.x + red.x) / 2, 3.4, (blue.z + red.z) / 2);
      this.mapRoot.add(label);
      this.disposables.push(label.material);
      if (label.material.map) this.disposables.push(label.material.map);
    }

    // Faint grid so movement reads clearly against untextured surfaces.
    const grid = new THREE.GridHelper(
      map.gridExtent * 2,
      map.gridExtent * 2,
      0xffffff,
      0xffffff,
    );
    grid.position.y = 0.02;
    const gridMaterial = grid.material as THREE.Material;
    gridMaterial.transparent = true;
    gridMaterial.opacity = 0.07;
    this.disposables.push(gridMaterial, grid.geometry);
    this.mapRoot.add(grid);
  }

  private lightViewModel(light: GameMap['lighting']): void {
    // Keyed from the left of the camera, matching where the weapon sits, plus
    // generous fill — the view model is always in frame and reads better a
    // little brighter than the world around it.
    this.viewModelLights.add(
      new THREE.HemisphereLight(light.skyColor, light.groundColor, 2.0),
    );
    this.viewModelLights.add(new THREE.AmbientLight(light.ambientColor, 1.2));
    const key = new THREE.DirectionalLight(light.sunColor, 2.2);
    key.position.set(-0.6, 1, 0.5);
    this.viewModelLights.add(key);
  }

  /**
   * Player's FOV preference. The next `setFov` picks it up, which happens every
   * frame, so there is nothing to invalidate here.
   */
  setBaseFov(degrees: number): void {
    this.baseFov = degrees;
  }

  /** `fovScale` comes from the equipped weapon's ADS zoom. */
  setFov(fovScale: number): void {
    const target = this.baseFov * fovScale;
    if (Math.abs(this.camera.fov - target) < 0.01) return;
    this.camera.fov = target;
    this.camera.updateProjectionMatrix();
  }

  /** Per-frame ambience: pad rings and beams breathe on a slow sine. */
  animate(nowMs: number): void {
    if (this.pulsingPads.length === 0) return;
    const pulse = 0.75 + 0.25 * Math.sin(nowMs / 450);
    for (const pad of this.pulsingPads) {
      pad.material.opacity = pad.base * pulse;
    }
  }

  resize(): void {
    const width = this.canvas.clientWidth || innerWidth;
    const height = this.canvas.clientHeight || innerHeight;
    this.renderer.setSize(width, height, false);
    const aspect = width / height;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.viewModelCamera.aspect = aspect;
    this.viewModelCamera.updateProjectionMatrix();
  }

  render(): void {
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    // Clear depth only, so the weapon never intersects walls.
    this.renderer.clearDepth();
    this.renderer.render(this.viewModelScene, this.viewModelCamera);
  }

  /** Drops map geometry and lights, keeping everything other systems added. */
  private clearMap(): void {
    this.mapRoot.clear();
    this.viewModelLights.clear();
    this.pulsingPads.length = 0;
    for (const item of this.disposables) item.dispose();
    this.disposables.length = 0;
  }

  dispose(): void {
    this.clearMap();
    this.renderer.dispose();
  }
}
